import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUp, Bookmark, CornerDownRight, Zap, ExternalLink, History, Maximize2, RotateCcw, SquarePen, Timer, TriangleAlert, X } from "lucide-react";
import { AiIcon } from "@/components/ai/AiIcon";
import { useAskPanel, type AskRequest } from "@/components/ai/AskPanelContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useShell } from "@/components/shell/ShellContext";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { aiFeedback, useAiFeature } from "@/lib/ai";
import { openWithConfig } from "@/lib/openWithConfig";
import { useSavedViews, type SavedView } from "@/lib/useSavedViews";
import { cn } from "@/lib/utils";
import { PlannedActions, type PlannedView } from "@/components/ai/PlannedActions";
import { fullPlan, runPlan, useAskCatalog, type Answer, type Plan } from "@/components/ai/askReport";
import { ReportBar } from "@/components/ai/AskReportControls";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { GroupTable, RecordsTable, ReportChart, ReportSummary, valueFormat } from "@/components/ai/AskReportView";

/** One turn of the conversation: the person's question, or the AI's answer to it */
type Msg =
  | { id: string; role: "user"; text: string; report?: string; about?: string }
  | { id: string; role: "assistant"; pending?: boolean; startedAt?: number; tookMs?: number; status?: string; draft?: string; answer?: Answer; error?: string; retry?: string; savedAs?: string | null; reply?: string; actions?: PlannedView[] };
type AssistantMsg = Extract<Msg, { role: "assistant" }>;

const EXAMPLES = [
  { title: "My open deals over 1 lakh", hint: "biggest first" },
  { title: "Hot leads with no call in the last 7 days", hint: "to call today" },
  { title: "Won deals this month vs last month, by person", hint: "with the change" },
  { title: "Win rate by person this quarter", hint: "won out of won and lost" },
  { title: "Top products ordered this month", hint: "value and quantity" },
  { title: "Quotes sent more than a week ago with no answer", hint: "to follow up" },
];
/** With the assistant on, examples of things it can do as well as answer */
const AGENT_EXAMPLES = [
  { title: "Create a lead for Prabhu from Iclass Research, Sulur, hot, wants a website", hint: "and fills in the details" },
  { title: "Add 10 licences to the Kovai Mills quote and give 5% off", hint: "shows the new total first" },
  { title: "Turn the Iclass Research lead into a ₹3 lakh deal closing next month", hint: "qualifies it and makes the deal" },
  { title: "Add a note to Kovai Mills: wants the price list by Friday", hint: "on the timeline" },
];
const SESSION_KEY = "ask.session";
const HISTORY_KEY = "ask.history";
const CHAT_KEY = "ask.chat";
/** The server refuses longer questions (AskCrm.service) */
const MAX_CHARS = 300;
/** The assistant takes longer instructions */
const AGENT_MAX_CHARS = 1000;
const inr = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const newId = () => Math.random().toString(36).slice(2, 10);

function load<T>(store: () => Storage, key: string, fallback: T): T {
  try {
    return JSON.parse(store().getItem(key) || "null") ?? fallback;
  } catch {
    return fallback;
  }
}
function keep(store: () => Storage, key: string, value: unknown) {
  try {
    store().setItem(key, JSON.stringify(value));
  } catch {
    /* not remembered; fine */
  }
}

/** Record pages the assistant can be told about ("add a note here", "move this to negotiation") */
const ABOUT_PATHS: Record<string, string> = { leads: "lead", deals: "deal", accounts: "account", contacts: "contact", quotes: "quote", proposals: "proposal" };
const WIDTH_KEY = "ask.panelWidth";
const MIN_W = 340;
const MAX_W = 760;

/**
 * Ask AI, docked beside every page (full screen on phones): the chat carries on while you move between pages, and on
 * a lead, deal, account… the assistant knows which record you mean. The AI only picks the search; answers come from
 * the records this person may see. The conversation lasts for this browser tab; saved reports and recent questions stay.
 */
export function AskPanel() {
  const { open: shown, closeAsk, request, doneRequest } = useAskPanel();
  // Mounted from the first open, then only hidden, so an answer on its way isn't lost by closing the panel
  const [mounted, setMounted] = useState(shown);
  useEffect(() => {
    if (shown) setMounted(true);
  }, [shown]);
  const [width, setWidth] = useState(() => Math.min(MAX_W, Math.max(MIN_W, Number(load<number>(() => localStorage, WIDTH_KEY, 420)) || 420)));

  /** Dragging the left edge resizes the panel; the width is remembered */
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = width;
    let w = w0;
    const move = (ev: PointerEvent) => {
      w = Math.min(MAX_W, Math.max(MIN_W, w0 + (x0 - ev.clientX)));
      setWidth(w);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = "";
      keep(() => localStorage, WIDTH_KEY, w);
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  if (!mounted) return null;
  return (
    <aside
      aria-label="Ask AI"
      style={{ "--ask-w": `${width}px` } as React.CSSProperties}
      className={cn(
        // Phones: full screen. Up to 1600px wide: floats over the right of the page. Wider: docked beside the page
        "fixed inset-0 z-40 flex-col bg-background sm:left-auto sm:w-[var(--ask-w)] sm:border-l sm:border-border sm:shadow-2xl min-[1600px]:relative min-[1600px]:inset-auto min-[1600px]:z-auto min-[1600px]:shrink-0 min-[1600px]:shadow-none",
        shown ? "flex" : "hidden",
      )}
    >
      <div role="separator" aria-orientation="vertical" aria-label="Resize" title="Drag to resize" onPointerDown={startResize} className="absolute inset-y-0 -left-1 z-10 hidden w-2 cursor-col-resize hover:bg-primary/20 sm:block" />
      <AskChat onClose={closeAsk} request={request} doneRequest={doneRequest} />
    </aside>
  );
}

function AskChat({ onClose, request, doneRequest }: { onClose: () => void; request: AskRequest | null; doneRequest: () => void }) {
  const askOn = useAiFeature("ASK_CRM");
  const agentFeature = useAiFeature("AGENT");
  const { hasPermission } = useAuth();
  const { recordLabel } = useShell();
  const { pathname } = useLocation();
  // The assistant that makes changes (it asks before saving); otherwise questions only
  const agentOn = agentFeature && hasPermission("ai_agent.use");
  const available = askOn || agentOn;
  const [sessionId, setSessionId] = useState<string | null>(() => load<string | null>(() => sessionStorage, SESSION_KEY, null));
  const reports = useSavedViews("ask");
  const [msgs, setMsgs] = useState<Msg[]>(() => load<Msg[]>(() => sessionStorage, CHAT_KEY, []).filter((m) => !(m.role === "assistant" && m.pending)));
  const [history, setHistory] = useState<string[]>(() => load<string[]>(() => localStorage, HISTORY_KEY, []).slice(0, 8));
  const [draft, setDraft] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const busy = msgs.some((m) => m.role === "assistant" && m.pending);
  const open = msgs.find((m): m is Extract<Msg, { role: "assistant" }> => m.id === openId && m.role === "assistant" && !!m.answer?.answerable);

  // The record on screen, unless the person took it off for this page
  const [droppedFor, setDroppedFor] = useState<string | null>(null);
  const m = pathname.match(/^\/(leads|deals|accounts|contacts|quotes|proposals)\/([0-9a-f-]{36})(?:\/|$)/i);
  const kind = m ? ABOUT_PATHS[m[1]!.toLowerCase()] : undefined;
  const about = agentOn && m && kind && droppedFor !== pathname ? { key: `${kind}:${m[2]}`, label: recordLabel || `this ${kind}` } : null;

  useEffect(() => {
    keep(() => sessionStorage, CHAT_KEY, msgs.filter((m) => !(m.role === "assistant" && m.pending)).slice(-30));
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [msgs]);

  const update = (id: string, patch: Partial<Extract<Msg, { role: "assistant" }>>) => setMsgs((cur) => cur.map((m) => (m.id === id ? ({ ...m, ...patch } as Msg) : m)));

  /** Adds the question and a waiting answer, then fills the answer in (run gets the answer's id, to show progress on it) */
  const send = async (user: Extract<Msg, { role: "user" }>, run: (id: string) => Promise<Partial<AssistantMsg>>, savedAs: string | null = null) => {
    const reply = newId();
    const startedAt = Date.now();
    setMsgs((cur) => [...cur, user, { id: reply, role: "assistant", pending: true, startedAt }]);
    try {
      const patch = await run(reply);
      update(reply, { pending: false, savedAs, tookMs: Date.now() - startedAt, status: undefined, draft: undefined, ...patch });
    } catch (e: any) {
      update(reply, { pending: false, tookMs: Date.now() - startedAt, error: e.message || "Couldn't answer that. Please try again.", retry: user.report ? undefined : user.text });
    }
  };

  const ask = (text: string) => {
    const q = text.trim();
    if (q.length < 3 || busy) return;
    setDraft("");
    const next = [q, ...history.filter((h) => h !== q)].slice(0, 8);
    setHistory(next);
    keep(() => localStorage, HISTORY_KEY, next);
    // The last answer in this chat, so "only the big ones" or "same for last month" builds on it
    const last = [...msgs].reverse().find((m): m is Extract<Msg, { role: "assistant" }> => m.role === "assistant" && !!m.answer?.answerable && !!m.answer.plan);
    const previous = last?.answer ? { question: last.answer.question, plan: last.answer.plan } : undefined;
    if (agentOn) {
      // The assistant: answers, looks things up and prepares changes; the conversation is kept on the server
      void send({ id: newId(), role: "user", text: q, about: about?.label }, async (id) => {
        // Streamed: what it is doing, then the reply's words as they are written
        const r = await streamAgent({ text: q, session_id: sessionId, about: about?.key }, (e) => {
          if (e.type === "status") update(id, { status: e.text });
          else if (e.type === "step") update(id, { draft: "" });
          else if (e.type === "text") setMsgs((cur) => cur.map((m) => (m.id === id && m.role === "assistant" ? { ...m, draft: (m.draft || "") + e.delta } : m)));
        });
        setSessionId(r.session_id);
        keep(() => sessionStorage, SESSION_KEY, r.session_id);
        return { reply: r.reply, answer: r.answers?.[0], actions: r.actions?.length ? r.actions : undefined };
      });
      return;
    }
    void send({ id: newId(), role: "user", text: q }, async () => ({ answer: unwrapObject<Answer>(await api.post("/api/Ai/Ask", { question: q, previous })) }));
  };

  /** A saved report runs its stored search again: instant, free, and within what this person may see */
  const runReport = (v: SavedView) => {
    if (busy) return;
    const question = String(v.config.question || v.name);
    void send(
      { id: newId(), role: "user", text: question, report: v.name },
      async () => ({ answer: { ...unwrapObject<Answer>(await api.post("/api/Ai/Ask/Run", { plan: v.config.plan })), question, title: v.name } }),
      v.name,
    );
  };

  const newChat = () => {
    setMsgs([]);
    setSessionId(null);
    keep(() => sessionStorage, SESSION_KEY, null);
    setOpenId(null);
    setDraft("");
    setTimeout(() => box.current?.focus(), 0);
  };

  // A question from Ctrl K or a saved report from a link: done once, when the panel is ready for it
  useEffect(() => {
    if (!request || !available) return;
    if (request.q) {
      doneRequest();
      ask(request.q);
      return;
    }
    const v = reports.views.find((x) => x.id === request.report);
    if (!v) return;
    doneRequest();
    runReport(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, available, reports.views]);

  const mine = reports.views.filter((v) => v.mine !== false);
  const shared = reports.views.filter((v) => v.mine === false);
  const composer = (
    <Composer ref={box} value={draft} onChange={setDraft} onSend={() => ask(draft)} busy={busy} autoFocus max={agentOn ? AGENT_MAX_CHARS : MAX_CHARS} placeholder={agentOn ? "Ask a question or tell me what to do…" : undefined} />
  );
  const aboutChip = about && (
    <div className="mb-1.5 flex">
      <span className="flex max-w-full items-center gap-1 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-[11.5px]" title="I'll take “this” and “here” to mean this record">
        <span className="text-muted-foreground">About:</span>
        <span className="truncate font-medium">{about.label}</span>
        <button type="button" aria-label="Don't use this record" onClick={() => setDroppedFor(pathname)} className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="size-3" />
        </button>
      </span>
    </div>
  );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(e) => {
        // Esc closes the panel, but not while a dialog or the full answer is open
        if (e.key === "Escape" && !openId && !(e.target as HTMLElement).closest?.('[role="dialog"],[role="menu"]')) onClose();
      }}
    >
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border px-3">
        <AiIcon className="size-5" />
        <h2 className="ml-1 flex-1 text-[14px] font-semibold">Ask AI</h2>
        {mine.length + shared.length + history.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Saved reports and recent questions" title="Saved reports and recent questions">
                <History className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
              {mine.length + shared.length > 0 && <DropdownMenuLabel className="text-xs text-muted-foreground">Saved reports</DropdownMenuLabel>}
              {[...mine, ...shared].map((v) => (
                <DropdownMenuItem key={v.id} onSelect={() => runReport(v)} className="group cursor-pointer gap-2" title={v.shared_by ? `Shared by ${v.shared_by}` : String(v.config.question || "")}>
                  <Bookmark className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{v.name}</span>
                  {v.mine === false ? (
                    <span className="shrink-0 text-[10.5px] text-muted-foreground">{v.shared_by || "shared"}</span>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Delete ${v.name}`}
                      title="Delete this report"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        void reports.remove(v.id);
                      }}
                      className="text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100 group-focus:opacity-100"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </DropdownMenuItem>
              ))}
              {history.length > 0 && (
                <>
                  {mine.length + shared.length > 0 && <DropdownMenuSeparator />}
                  <div className="flex items-center justify-between pr-2">
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Recent questions</DropdownMenuLabel>
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setHistory([]);
                        keep(() => localStorage, HISTORY_KEY, []);
                      }}
                    >
                      Clear
                    </button>
                  </div>
                  {history.map((h) => (
                    <DropdownMenuItem key={h} onSelect={() => ask(h)} title={h} className="cursor-pointer text-muted-foreground">
                      <span className="truncate">{h}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button variant="ghost" size="icon-sm" onClick={newChat} aria-label="New chat" title="New chat">
          <SquarePen className="size-4" />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close" title="Close (Esc or Ctrl J)">
          <X className="size-4" />
        </Button>
      </div>

      {!available ? (
        <div className="m-auto max-w-sm p-6 text-center text-sm text-muted-foreground">Ask AI is switched off. A Super Admin can switch it on in Settings → AI assistant.</div>
      ) : msgs.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4">
          <div className="my-auto w-full py-6">
            <div className="mb-5 flex flex-col items-center text-center">
              <AiIcon className="size-9" />
              <h1 className="mt-3 text-lg font-semibold tracking-tight">{agentOn ? "What can I do for you?" : "What would you like to know?"}</h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {agentOn ? "Ask a question, or tell me what to do: create a lead, set a follow-up, add a note. I show every change before it's saved." : "Ask about your leads, deals, quotes, orders, tasks, visits, calls, accounts or contacts. Open any answer to change it into the report you need. Answers come only from records you can see."}
              </p>
            </div>
            {aboutChip}
            {composer}
            <div className="mt-3 grid gap-2">
              {(agentOn ? AGENT_EXAMPLES : EXAMPLES).map((e) => (
                <button key={e.title} type="button" onClick={() => ask(e.title)} className="rounded-xl border border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-accent/40">
                  <div className="text-[13px] font-medium">{e.title}</div>
                  <div className="text-xs text-muted-foreground">{e.hint}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-4">
            <div className="space-y-5 py-4">
              {msgs.map((m) =>
                m.role === "user" ? (
                  <UserBubble key={m.id} msg={m} />
                ) : (
                  <AssistantTurn key={m.id} msg={m} onOpen={() => setOpenId(m.id)} onRetry={(q) => ask(q)} onActions={(next) => update(m.id, { actions: next })} />
                ),
              )}
            </div>
          </div>
          <div className="px-3 pb-3 pt-1">
            {aboutChip}
            {composer}
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              {agentOn ? "Nothing changes in the CRM until you press Confirm." : 'Follow-ups build on the last answer ("only the big ones"). Check "How I read it" before you act.'}
            </p>
          </div>
        </>
      )}

      {open?.answer && (
        <ArtifactPanel
          key={open.id}
          answer={open.answer}
          savedAs={open.savedAs ?? null}
          onClose={() => setOpenId(null)}
          onSave={async (name, share) => {
            await reports.save(name, { question: open.answer!.question, plan: open.answer!.plan }, share);
            update(open.id, { savedAs: name });
          }}
          onEdit={async (plan) => {
            const a = await runPlan(plan);
            update(open.id, { answer: { ...a, question: open.answer!.question, title: plan.title || open.answer!.title, suggestion_id: open.answer!.suggestion_id }, savedAs: null });
          }}
        />
      )}
    </div>
  );
}

/** The question box: grows with the text, Enter sends, Shift+Enter starts a new line, shows characters used */
const Composer = React.forwardRef<HTMLTextAreaElement, { value: string; onChange: (v: string) => void; onSend: () => void; busy: boolean; autoFocus?: boolean; max?: number; placeholder?: string }>(function Composer({ value, onChange, onSend, busy, autoFocus, max = MAX_CHARS, placeholder = "Ask about your leads, deals, quotes…" }, ref) {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const t = inner.current;
    if (!t) return;
    t.style.height = "auto";
    t.style.height = `${Math.min(t.scrollHeight, 180)}px`;
  }, [value]);
  const ready = value.trim().length >= 3 && !busy;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) onSend();
      }}
      className="rounded-2xl border border-border bg-card p-2 shadow-sm transition-shadow focus-within:border-primary/40 focus-within:shadow-md"
    >
      <textarea
        ref={(el) => {
          inner.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (ready) onSend();
          }
        }}
        rows={1}
        maxLength={max}
        autoFocus={autoFocus}
        aria-label="Your question"
        placeholder={placeholder}
        className="block max-h-[180px] min-h-[44px] w-full resize-none bg-transparent px-2 py-2 text-[15px] outline-none placeholder:text-muted-foreground"
      />
      <div className="flex items-center justify-between gap-2 pl-2">
        <span className="text-[11px] text-muted-foreground">Enter to send · Shift+Enter for a new line</span>
        <div className="flex items-center gap-2">
          <span aria-live="polite" title={`Up to ${max} characters`} className={cn("font-mono text-[11px] tabular-nums text-muted-foreground", value.length >= max ? "text-destructive" : value.length >= max - 40 && "text-warning")}>
            {value.length} / {max}
          </span>
          <Button type="submit" size="icon" className="size-8 rounded-full" disabled={!ready} aria-label="Ask">
            <ArrowUp className="size-4" />
          </Button>
        </div>
      </div>
    </form>
  );
});

function UserBubble({ msg }: { msg: Extract<Msg, { role: "user" }> }) {
  return (
    <div className="flex flex-col items-end">
      {msg.report && (
        <span className="mb-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Bookmark className="size-3" /> Saved report · {msg.report}
        </span>
      )}
      {msg.about && <span className="mb-1 truncate text-[11px] text-muted-foreground">About {msg.about}</span>}
      <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-muted px-4 py-2.5 text-[14px]">{msg.text}</div>
    </div>
  );
}

/** 0.8s, 6.2s, 14s, 1m 05s */
function took(ms: number) {
  const s = ms / 1000;
  if (s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${String(Math.round(s % 60)).padStart(2, "0")}s`;
}

type AgentReply = { session_id: string; reply: string; answers: Answer[]; actions: PlannedView[]; reused?: boolean };
type AgentEvent = { type: "status"; text: string } | { type: "step" } | { type: "text"; delta: string };

/**
 * A message to the assistant over its streaming endpoint: status lines and the reply's words arrive as they happen
 * (onEvent), then the full answer. A server without the stream is asked the plain way.
 */
async function streamAgent(body: { text: string; session_id: string | null; about?: string }, onEvent: (e: AgentEvent) => void): Promise<AgentReply> {
  const res = await fetch("/api/Ai/Agent/Stream", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", Accept: "text/event-stream" }, body: JSON.stringify(body) });
  if (res.status === 404) return unwrapObject<AgentReply>(await api.post("/api/Ai/Agent/Message", body));
  if (!res.ok || !res.body) {
    let message = "The assistant didn't answer. Please try again.";
    try {
      const j = await res.json();
      message = j?.Message || j?.message || message;
    } catch {
      // not JSON
    }
    throw new Error(Array.isArray(message) ? message[0] : message);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (value) buf += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      const event = /^event: (.*)$/m.exec(chunk)?.[1];
      const raw = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!event || raw === undefined) continue;
      const data = JSON.parse(raw);
      if (event === "done") return data as AgentReply;
      if (event === "error") throw new Error(data?.message || "The assistant didn't answer. Please try again.");
      onEvent(data as AgentEvent);
    }
    if (done) throw new Error("The answer was cut off. Please try again.");
  }
}

/** While the answer is on its way: what it is doing and a running clock, so a longer wait doesn't look stuck */
function Working({ startedAt, status, draft }: { startedAt?: number; status?: string; draft?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(t);
  }, []);
  const ms = Math.max(0, now - (startedAt ?? now));
  const step = status || (ms < 3000 ? "Reading your question…" : ms < 8000 ? "Looking through your records…" : ms < 20000 ? "Putting the answer together…" : "Taking a little longer than usual…");
  return (
    <div className="space-y-1.5 pt-1">
      {draft && <p className="whitespace-pre-wrap">{draft}</p>}
      <p className="flex items-center gap-2 text-muted-foreground" aria-live="polite">
        <span>{step}</span>
        <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] tabular-nums">
          <Timer className="size-3" /> {took(ms)}
        </span>
      </p>
    </div>
  );
}

function AssistantTurn({ msg, onOpen, onRetry, onActions }: { msg: AssistantMsg; onOpen: () => void; onRetry: (q: string) => void; onActions: (next: PlannedView[]) => void }) {
  const a = msg.answer;
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent">
        <AiIcon className="size-4" busy={msg.pending} />
      </span>
      <div className="min-w-0 flex-1 space-y-2.5 text-[14px]">
        {msg.pending ? (
          <Working startedAt={msg.startedAt} status={msg.status} draft={msg.draft} />
        ) : msg.error ? (
          <div className="space-y-2 pt-1">
            <p className="text-destructive">{msg.error}</p>
            {msg.retry && (
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onRetry(msg.retry!)}>
                <RotateCcw className="size-3.5" /> Try again
              </Button>
            )}
          </div>
        ) : msg.reply && !a ? (
          <p className="whitespace-pre-wrap pt-1">{msg.reply}</p>
        ) : a && !a.answerable ? (
          <p className="pt-1">{a.reply}</p>
        ) : a ? (
          <>
            {a.reused && (
              <p className="flex items-center gap-1 pt-1 text-[11.5px] text-muted-foreground" title="The AI's reading of this question was reused; the records are looked up fresh">
                <Zap className="size-3.5" /> Same question as earlier today, answered instantly
              </p>
            )}
            {msg.reply && <p className="whitespace-pre-wrap pt-1">{msg.reply}</p>}
            {a.plan?.follows_previous === true && (
              <p className="flex items-center gap-1 pt-1 text-[11.5px] text-muted-foreground">
                <CornerDownRight className="size-3.5" /> Building on your last answer
              </p>
            )}
            <p className="pt-1">
              <span className="font-semibold">{a.answer}</span>
              {a.measure === "LIST" && (a.rows?.length || 0) > 0 && <span className="text-muted-foreground"> · {a.title}</span>}
            </p>
            {(a.unmatched || []).map((u) => (
              <p key={u} className="flex items-center gap-1.5 text-[13px] text-warning">
                <TriangleAlert className="size-3.5" /> {u}
              </p>
            ))}
            <AnswerCard answer={a} onOpen={onOpen} />
            <div className="flex flex-wrap items-center gap-1 text-[11.5px] text-muted-foreground">
              <span>How I read it:</span>
              {(a.read || []).map((r) => (
                <span key={r} className="rounded-full bg-muted px-2 py-0.5">
                  {r}
                </span>
              ))}
            </div>
          </>
        ) : null}
        {!msg.pending && msg.actions?.length ? <PlannedActions actions={msg.actions} onChange={onActions} /> : null}
        {!msg.pending && msg.tookMs != null && (
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground" title="From sending the question to the answer">
            <Timer className="size-3" /> {msg.error ? "Stopped after" : "Answered in"} {took(msg.tookMs)}
          </p>
        )}
      </div>
    </div>
  );
}

/** The answer in the chat: the first few records, then the full answer or the list it came from */
function AnswerCard({ answer, onOpen }: { answer: Answer; onOpen: () => void }) {
  const navigate = useNavigate();
  const rows = answer.rows || [];
  const top = rows.slice(0, 5);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium">{answer.title}</div>
          <div className="text-[11.5px] text-muted-foreground">
            {answer.groups?.length ? "Breakdown · " : ""}
            {answer.count || 0} {answer.count === 1 ? "record" : "records"}
          </div>
        </div>
        <Button size="icon-sm" variant="ghost" onClick={onOpen} aria-label="See the full answer" title="See the full answer">
          <Maximize2 className="size-3.5" />
        </Button>
      </div>
      {(answer.groups || []).length > 0 && (
        <ul className="space-y-1 border-b border-border px-3 py-2 text-[12.5px]">
          {answer.groups!.slice(0, 6).map((g) => (
            <li key={`${g.key}|${g.key2 ?? ""}|${g.label}`} className="flex justify-between gap-2">
              <span className="truncate">{g.label2 ? `${g.label} · ${g.label2}` : g.label}</span>
              <span className="shrink-0 font-mono text-[11.5px]">
                {answer.measure === "RATE" || answer.measure === "AVERAGE" ? valueFormat(answer)(g.value) : g.count}
                {answer.measure !== "RATE" && answer.measure !== "AVERAGE" && g.sum != null && g.sum > 0 ? ` · ${inr(g.sum)}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {top.length > 0 && (
        <ul className="divide-y divide-border">
          {top.map((r) => (
            <li key={r.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px]">
              <div className="min-w-0 flex-1">
                <Link to={r.link} className="block truncate font-medium hover:text-primary hover:underline">
                  {r.title}
                </Link>
                {(r.status || r.subtitle) && <div className="truncate text-[11px] text-muted-foreground">{[r.status, r.subtitle].filter(Boolean).join(" · ")}</div>}
              </div>
              {r.amount != null && <span className="shrink-0 font-mono text-[11.5px]">{inr(r.amount)}</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-border px-3 py-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onOpen}>
          {(answer.count || 0) > top.length ? `See all ${answer.count}` : "Open report"}
        </Button>
        {answer.open_in && (
          <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => openWithConfig(navigate, answer.open_in!.path, answer.open_in!.config)} title={answer.open_in.exact ? `Open these in the ${answer.open_in.label} list` : `Not on that list: ${answer.open_in.missing.join(", ")}`}>
            <ExternalLink className="size-3.5" /> Open in {answer.open_in.label}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The full answer over the page (full screen on phones), as a report the person can change: take conditions off, add
 * filters and has / has-no checks, pick columns, break it down, compare, see targets. Each change runs the search again
 * without the AI. Then open it in the list, save it as a report or download it.
 */
function ArtifactPanel({ answer, savedAs, onClose, onSave, onEdit }: { answer: Answer; savedAs: string | null; onClose: () => void; onSave: (name: string, share: "team" | "company" | null) => Promise<void>; onEdit: (plan: Plan) => Promise<void> }) {
  const navigate = useNavigate();
  const { error } = useToast();
  const catalog = useAskCatalog(!!answer.plan);
  const [rated, setRated] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const plan = answer.plan ? fullPlan(answer.plan) : null;
  const rows = answer.rows || [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && !(e.target as HTMLElement).closest?.('[role="menu"],[role="dialog"]:not([data-artifact])') && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const edit = async (next: Plan) => {
    if (busy) return;
    setBusy(true);
    try {
      await onEdit(next);
    } catch (e: any) {
      error("Couldn't update the report", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };
  const rate = (good: boolean) => {
    setRated(good);
    aiFeedback(answer.suggestion_id, good);
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex lg:items-center lg:justify-center lg:bg-black/40 lg:p-8" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
    <aside data-artifact role="dialog" aria-modal="true" aria-busy={busy} className="flex w-full flex-col bg-background lg:max-h-full lg:max-w-[1100px] lg:overflow-hidden lg:rounded-xl lg:border lg:border-border lg:shadow-2xl" aria-label={answer.title}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-semibold">{answer.title}</h2>
          <p className={cn("font-mono text-sm font-semibold text-primary transition-opacity", busy && "opacity-50")}>{answer.answer}</p>
        </div>
        <button type="button" aria-label="Close" title="Close (Esc)" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
          <X className="size-4" />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-2.5">
        {answer.open_in && (
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => openWithConfig(navigate, answer.open_in!.path, answer.open_in!.config)} title={answer.open_in.exact ? `Open these in the ${answer.open_in.label} list` : `Not on that list: ${answer.open_in.missing.join(", ")}`}>
            <ExternalLink className="size-3.5" /> Open in {answer.open_in.label}
          </Button>
        )}
        {savedAs ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Bookmark className="size-3.5" /> Saved as “{savedAs}”
          </span>
        ) : (
          answer.plan && (
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setSaving(true)} disabled={busy}>
              <Bookmark className="size-3.5" /> Save as report
            </Button>
          )
        )}
        {answer.plan && (
          <ExportMenu post={{ url: "/api/Ai/Ask/Download", body: (format) => ({ plan: answer.plan, format }) }} label="Uses the conditions shown (up to 2000 records)" disabled={busy} />
        )}
        {busy && <span className="text-xs text-muted-foreground" aria-live="polite">Updating…</span>}
      </div>

      <div className={cn("min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 transition-opacity", busy && "opacity-60")}>
        {plan && catalog && <ReportBar plan={plan} answer={answer} catalog={catalog} busy={busy} onChange={(next) => void edit(next)} />}
        {(answer.unmatched || []).map((u) => (
          <p key={u} className="flex items-center gap-1.5 text-[13px] text-warning">
            <TriangleAlert className="size-3.5" /> {u}
          </p>
        ))}
        {answer.open_in && !answer.open_in.exact && <p className="text-xs text-muted-foreground">The {answer.open_in.label} list can't show {answer.open_in.missing.join(", ")}.</p>}
        <ReportSummary answer={answer} />
        {plan && <ReportChart answer={answer} plan={plan} />}
        <GroupTable answer={answer} />
        {plan ? (
          <RecordsTable answer={answer} plan={plan} busy={busy} onSort={(patch) => void edit({ ...plan, ...patch })} onOpen={onClose} />
        ) : (
          rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing matches. Try a wider question.</p>
        )}
        {(answer.count || 0) > rows.length && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              Showing {rows.length} of {answer.count}.
            </span>
            {plan && rows.length < 100 && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={() => void edit({ ...plan, limit: Math.min(100, Math.max(rows.length, 10) + 50) })}>
                Show more
              </Button>
            )}
            <span>Open in the list, or export as PDF or Excel for up to 2000.</span>
          </div>
        )}
      </div>

      {answer.suggestion_id && (
        <div className="flex items-center gap-2 border-t border-border px-5 py-2.5 text-xs text-muted-foreground">
          <span>Did I read the question right?</span>
          {[true, false].map((good) => (
            <button
              key={String(good)}
              type="button"
              disabled={rated !== null}
              onClick={() => rate(good)}
              className={cn("rounded-full border border-border px-2.5 py-0.5 hover:bg-muted disabled:opacity-60", rated === good && "border-primary text-primary")}
            >
              {good ? "Yes" : "No"}
            </button>
          ))}
          {rated === false && <span>Thanks. Change the report above, or try saying it another way.</span>}
        </div>
      )}
      {saving && <SaveReportDialog defaultName={answer.title} onClose={() => setSaving(false)} onSave={onSave} />}
    </aside>
    </div>,
    document.body,
  );
}

/** Names the answer and keeps it as a report; Sales Heads can share with their team, Super Admins with everyone */
function SaveReportDialog({ defaultName, onClose, onSave }: { defaultName: string; onClose: () => void; onSave: (name: string, share: "team" | "company" | null) => Promise<void> }) {
  const { user } = useAuth();
  const { success, error } = useToast();
  const role = String((user as any)?.UserRole?.RoleCode || "").toUpperCase();
  const canShare = role === "SUPER_ADMIN" || role === "SALES_HEAD";
  const [name, setName] = useState(defaultName.slice(0, 60));
  const [share, setShare] = useState<"" | "team" | "company">("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(name.trim(), share || null);
      success("Report saved", share ? `"${name.trim()}" is shared with ${share === "company" ? "everyone" : "your team"}; each person sees only their own records in it.` : `"${name.trim()}" is in your saved reports.`);
      onClose();
    } catch (err: any) {
      error("Couldn't save the report", err?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Save as report</DialogTitle>
            <DialogDescription>Runs this search again whenever you open it, with dates kept current (this month stays this month). It doesn't use the AI again.</DialogDescription>
          </DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required autoFocus aria-label="Report name" />
          {canShare && (
            <label className="block space-y-1.5 text-sm font-medium">
              <span>Share with</span>
              <select value={share} onChange={(e) => setShare(e.target.value as "" | "team" | "company")} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm font-normal text-foreground">
                <option value="">Only me</option>
                <option value="team">My team</option>
                {role === "SUPER_ADMIN" && <option value="company">Everyone</option>}
              </select>
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
